import React, { useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { makeStyles } from "@mui/styles";



import Button from "@mui/material/Button";

import { useHistory } from "react-router-dom";

import { NavLink } from "react-router-dom";

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useSelector, useDispatch } from "react-redux";
import { setOrderFromDate } from "store/actions/restaurant";

import { checkDelTransactionsPermission, checkStaffPermission } from "util/userUtils";

import { showErrorSnackbar, showSuccessSnackbar } from "store/actions/snackbarMessage";
import TransactionDeleteDialog from "./TransactionDeleteDialog";
import EmailInvoiceDialog from "./EmailInvoiceDialog";
import { sendInvoiceEmail } from "apis/restaurantAPIs";

const useStyles = makeStyles((theme) => ({
  root: {},
  row: {
    height: "42px",
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      // A fixed-height, non-wrapping row clips its buttons on a 344px foldable.
      height: "auto",
      flexWrap: "wrap",
      gap: theme.spacing(1),
    },
  },
  spacer: {
    flexGrow: 1,
    // A growing spacer inside a wrapping row renders as a blank line.
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  importButton: {
    marginRight: theme.spacing(1),
  },
  rightSpacer: {
    marginRight: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  exportButton: {
    marginRight: theme.spacing(1),
  },
  searchInput: {
    marginRight: theme.spacing(1),
  },
  // Button-side margin. Kept separate from `rightSpacer`, which is a layout
  // spacer element that gets hidden entirely on small screens.
  buttonSpacing: {
    marginRight: theme.spacing(1),
  },
  actionButton: {
    [theme.breakpoints.down("sm")]: {
      flex: "1 1 auto",
      width: "100%",
      marginRight: 0,
    },
  },
  actionLink: {
    [theme.breakpoints.down("sm")]: {
      flex: "1 1 auto",
      width: "100%",
      "& button": {
        width: "100%",
      },
    },
  },
}));

const TransactionsToolbar = (props) => {
  const { className, canDeleteTransactions, canDeleteInvoiceTransctions, ...rest } = props;
  const classes = useStyles();
  const history = useHistory();

  const dispatch = useDispatch();

  const [buttonLoading, setButtonLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const transactions = useSelector((state) => state.transaction.transactions);

  const setOrderDate = (date) => dispatch(setOrderFromDate(date));
  const selectedDate = useSelector((state) => state.restaurant.orderFromDate);
  
  // Ensure selectedDate is a valid Date object
  const validSelectedDate = selectedDate instanceof Date && !isNaN(selectedDate) ? selectedDate : new Date();
  const selectedTransactionIds = useSelector((state) => state.transaction.ids);
  const user = useSelector((state) => state.account.user);
  const showErrorMessage = (msg) => dispatch(showErrorSnackbar(msg));
  const showSuccessMessage = (msg) => dispatch(showSuccessSnackbar(msg));

  const handleDateChange = (date) => {
    // console.log("date", date);
    setOrderDate(date);
    history.push("/transactions");
  };

  const deleteTransactions = () => {
    //console.log("selectedTransactionIds", selectedTransactionIds);
    if (selectedTransactionIds && selectedTransactionIds.length === 0) {
      showErrorMessage("Please select a transaction.");
      return;
    }

    // check transaction is inovice printed or not
    const selectedTransactions = transactions.filter((transaction) =>
      selectedTransactionIds.includes(transaction.uuid)
    );

    const hasInvoicePrintedTransactions =
      selectedTransactions.filter((transaction) => transaction.isInvoicePrinted).length > 0;

    // console.log('Deleting transactions', selectedTransactionIds, hasInvoicePrintedTransactions, selectedTransactions, transactions);
    if (!canDeleteInvoiceTransctions && hasInvoicePrintedTransactions) {
      showErrorMessage("Cannot delete transactions with invoice printed");
      return;
    }

    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const deleteTran = () => {
    if (deleteReason.length === 0) {
      showErrorMessage("Please select or enter a reason.");
      return;
    }
    props.deleteProxy(deleteReason);
  };

  const formatDateToYearMonth = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const formatDateToTransactionDate = (date) => {
    const year = String(date.getFullYear()).slice(-2);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}-${month}-${year}`;
  };

  const handleEmailInvoice = async (email) => {
    setEmailLoading(true);
    setEmailError("");
    try {
      const yearMonthStr = formatDateToYearMonth(validSelectedDate);
      const transactionDateStr = formatDateToTransactionDate(validSelectedDate);
      const transactionId = selectedTransactionIds[0];

      await sendInvoiceEmail({
        yearMonthStr,
        transactionDateStr,
        email,
        transactionId
      });

      showSuccessMessage("Invoice email sent successfully");
      setEmailDialogOpen(false);
    } catch (error) {
      console.error("Error sending invoice email:", error);
      setEmailError(error.response?.data?.message || "Failed to send invoice email");
    } finally {
      setEmailLoading(false);
    }
  };

  const handleEmailDialogClose = () => {
    setEmailDialogOpen(false);
    setEmailError("");
  };

  return (
    <div {...rest} className={clsx(classes.root, className)}>
      <div className={classes.row}>
        <span className={classes.spacer} />

        {user && checkStaffPermission(user) ? (
          <>
            <NavLink to={"/staffActivities"} className={classes.actionLink}>
              <Button variant="outlined" color="primary">
                Staff Activities
              </Button>
            </NavLink>
            <span className={classes.rightSpacer} />
          </>
        ) : null}

        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            if (!selectedTransactionIds || selectedTransactionIds.length === 0) {
              showErrorMessage("Please select a transaction.");
              return;
            }
            setEmailDialogOpen(true);
          }}
          className={clsx(classes.buttonSpacing, classes.actionButton)}
        >
          Email Invoice
        </Button>

        {user && checkDelTransactionsPermission(user) && canDeleteTransactions === true ? (
         <Button variant="outlined" color="primary" onClick={deleteTransactions} disabled={buttonLoading} className={classes.actionButton}>
         Remove Invalid Transactions
       </Button>
          // <IconButton aria-label="delete" >
          //   <DeleteIcon />
          // </IconButton>
        ) : null}
      </div>
    
      <div className={classes.row}>
        {/* <SearchInput
          className={classes.searchInput}
          placeholder="Search order"
        /> */}

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            disableToolbar
            variant="inline"
            format="dd/MM/yyyy"
            margin="normal"
            autoOk={true}
            id="date-picker-inline"
            label="Transaction Date "
            value={validSelectedDate}
            onChange={handleDateChange}
            KeyboardButtonProps={{
              "aria-label": "change date",
            }}
          />
        </LocalizationProvider>
        <span className={classes.spacer} />
        <TransactionDeleteDialog
          open={deleteDialogOpen}
          deleteProxy={deleteTran}
          close={closeDeleteDialog}
          updateReason={setDeleteReason}
          reason={deleteReason}
        />
        <EmailInvoiceDialog
          open={emailDialogOpen}
          onClose={handleEmailDialogClose}
          onSend={handleEmailInvoice}
          loading={emailLoading}
          error={emailError}
        />
      </div>
     
    </div>
  );
};

TransactionsToolbar.propTypes = {
  className: PropTypes.string,
};

export default TransactionsToolbar;
